import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { AlertType, ModalComponent, ModalResult } from '@michelin/theme';
import { Store } from '@ngxs/store';
import _ from 'lodash-es';
import { Subject, Subscription, catchError, filter, of, switchMap } from 'rxjs';
import { ActionType, SpecLine, SpecificationType, emptyLimit, getActionType } from 'src/app/features/asset-management/models/SpecLine';
import { Specification } from 'src/app/features/asset-management/models/Specification';
import {
  UpdateSpecificationLine,
  currentSpecExceedsMichelinSpec,
  mapToUpdateSpecificationLine
} from 'src/app/features/asset-management/models/request/UpdateSpecificationLine';
import { AssetManagementService } from 'src/app/features/asset-management/services/asset-management.service';
import { prepareSpecificationLineLabels } from 'src/app/features/cam-specification/utils/SpecificationUtils';
import { ProductService } from 'src/app/shared/services/product.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { AssetManagementState, UpdateCurrentSpecificationAction, UpdateEditingModeAction } from 'src/app/shared/state/AssetManagementState';
import { DealerState } from 'src/app/shared/state/DealerState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { environment } from 'src/environments/environment';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { canEditCamSpec, populateLimitForm } from '../../utils/SpecificationUtils';
import { inject } from '@angular/core';

@Component({
  selector: 'app-view-edit-cam-spec-line',
  templateUrl: './view-edit-cam-spec-line.component.html',
  styleUrls: ['./view-edit-cam-spec-line.component.scss'],
  standalone: false
})
export class ViewEditCamSpecLineComponent implements OnInit, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();

  @ViewChild('saveConfirmationModal', { static: true }) saveConfirmationModal: ModalComponent;
  @ViewChild('saveConfirmation') contentModal: TemplateRef<any>;
  @ViewChild('deleteSpecLineModal', { static: true }) deleteSpecLineModal: ModalComponent;
  @ViewChild('deleteSpecLineContent') deleteSpecLineContent: TemplateRef<any>;
  @ViewChild('duplicateConfirmationModel', { static: true }) duplicateConfirmationModal: ModalComponent;
  @ViewChild('duplicateConfirmation') dupContentModal: TemplateRef<any>;

  specificationLineForm: FormGroup;
  specification: Specification | undefined;
  specificationLine: SpecLine | undefined;
  updateSpecificationLine: UpdateSpecificationLine;

  specificationLineId: string;
  lineName: string;
  createdDate: string;
  modifiedDate: string;
  exceedMichelinSpec: boolean;

  editMode = false;
  isDefault = true;
  hasEditPermission = true;
  isSaving = false;
  checkingProductCombination = false;
  isDeleting = false;
  username: string;

  addLineMode = false;
  secondRepairLimitAdded = false;

  private readonly michelinConstant = 'MICHELIN';
  private readonly nafaConstant = 'NAFA';
  private routerSubscription: Subscription;

  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public fb = inject(FormBuilder);
  protected translationService = inject(TranslationService);
  public utilityService = inject(UtilityService);
  public productService = inject(ProductService);
  public assetManagementService = inject(AssetManagementService);
  public store = inject(Store);
  public datePipe = inject(DatePipe);

  constructor() {
    this.specificationLineForm = this.fb.group({
      label: ['', Validators.required],
      type: [0],
      isDefault: [false],
      definition: this.utilityService.getDefinitionFormGroup(),
      ntr: this.utilityService.getActionDetailsFormGroup(),
      age: this.utilityService.getActionDetailsFormGroup(),
      limit0: this.utilityService.getLimitFormGroup(true),
      limit1: this.utilityService.getLimitFormGroup(false)
    });
  }

  ngOnInit(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        if (event.navigationTrigger === 'popstate') {
          this.router.navigateByUrl('/home/specifications');
        }
      });
    this.username = this.store.selectSnapshot(DealerState.currentUser);
    this.route.params.subscribe((params) => {
      this.specification = _.cloneDeep(this.store.selectSnapshot(AssetManagementState.currentSpecification));
      this.specificationLineId = params['specificationLineId'];
      this.specificationLine = this.specification.specificationLines.find((line) => line.id === this.specificationLineId);
      this.addLineMode = this.route.snapshot.queryParams['addNewLine'] === 'true';

      if (!this.addLineMode) {
        this.initialize();
      } else {
        this.addLineMode = true;
        this.editMode = true;
        this.isDefault = this.specificationLine.defaultSpec;
        this.populateFormGroup();
        this.specificationLineForm.get('label').patchValue('');
        this.specificationLineForm.markAsUntouched();
      }

      // setting the user edit permission
      const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
      this.hasEditPermission = canEditCamSpec(
        this.michelinConstant === this.specification.type,
        this.nafaConstant === this.specification.type,
        dealer
      );
    });
  }

  enableEdit() {
    if (environment.envName !== 'dev') {
      gtag('event', 'cam_edit_button_clicked', {
        event_category: 'BUTTON_CLICK',
        event_label: 'CAM Edit Button Click',
        value: `For dealer: ${window.localStorage.getItem('dealer')}, customer: ${window.localStorage.getItem('customer')}`
      });
    }
    this.editMode = true;
    this.store.dispatch(new UpdateEditingModeAction(true));

    const ntrFormControl =
      this.specificationLine.ntr.action === this.translationService.translateToEnglish(getActionType(ActionType.REJECT))
        ? 'disposition'
        : 'treads';
    const ageFormControl =
      this.specificationLine.age.action === this.translationService.translateToEnglish(getActionType(ActionType.REJECT))
        ? 'disposition'
        : 'treads';
    const limitFormControl =
      this.specificationLine.repair.action === this.translationService.translateToEnglish(getActionType(ActionType.REJECT))
        ? 'disposition'
        : 'treads';

    this.specificationLineForm.get('ntr').get(ntrFormControl).addValidators(Validators.required);
    this.specificationLineForm.get('age').get(ageFormControl).addValidators(Validators.required);
    this.specificationLineForm.get('limit0').get(limitFormControl).addValidators(Validators.required);
  }

  save() {
    if (this.isSaving) {
      return; // prevent multiple clicks while saving
    }

    if (environment.envName !== 'dev') {
      gtag('event', 'cam_save_button_clicked', {
        event_category: 'BUTTON_CLICK',
        event_label: 'CAM Save Button Click',
        value: `For dealer: ${window.localStorage.getItem('dealer')}, customer: ${window.localStorage.getItem('customer')}`
      });
    }

    let combinationExist = false;
    if (this.specificationLineForm.invalid) {
      this.specificationLineForm.markAllAsTouched();
      this.utilityService.showAlert(
        AlertType.strong,
        this.translationService.translate('incorrectValuesText'),
        this.translationService.translate('incorrectValuesAlertText')
      );
      return;
    }

    // verify if Make, series, size, tread, application and axle combination already exists for different CAM Spec line.
    combinationExist = this.utilityService.duplicateCombinationValidator(
      this.specificationLine,
      this.specification,
      this.specificationLineForm.value,
      this.store
    );

    if (combinationExist) {
      this.duplicateCheckModal();
      return of(true);
    }

    this.checkingProductCombination = true;

    // mapping specification line to the request payload
    this.updateSpecificationLine = mapToUpdateSpecificationLine(
      this.specificationLine,
      this.specificationLineForm.value,
      this.store,
      this.translationService
    );
    this.productService
      .isProductCombinationsValid(this.updateSpecificationLine, this.specificationLine)
      .pipe(
        switchMap((isValid) => {
          if (!isValid.data) {
            this.utilityService.showAlert(
              AlertType.strong,
              this.translationService.translate('incorrectCombinationText'),
              this.translationService.translate('incorrectCombinationAlertText')
            );
            return of(null);
          }
          this.exceedMichelinSpec = currentSpecExceedsMichelinSpec(
            this.specificationLineForm.value,
            this.specificationLine.limits.length,
            this.store
          );
          this.showSaveConfirmationModal();
          return of(true);
        }),
        catchError((error) => {
          console.log(error);
          return of(null);
        })
      )
      .subscribe(() => {
        this.checkingProductCombination = false;
      });
  }

  cancel() {
    this.editMode = false;
    this.store.dispatch(new UpdateEditingModeAction(false));

    if (!this.addLineMode) {
      if (this.secondRepairLimitAdded && this.specificationLine.limits.length === 2) {
        this.specificationLine.limits.splice(1, 1);
        this.secondRepairLimitAdded = false;
      }
      this.populateFormGroup();
    } else {
      this.addLineMode = false;
      this.secondRepairLimitAdded = false;
      this.specification.specificationLines.splice(0, 1);
      this.store.dispatch(new UpdateCurrentSpecificationAction(this.specification));
      this.router.navigate(['../', this.specification.specificationLines[0].id], { relativeTo: this.route });
    }
  }

  showSaveConfirmationModal(): void {
    this.saveConfirmationModal.open({
      id: 'save-confirmation',
      width: 550,
      // Remove with @EDIT-OFF
      title: this.translationService.translate('confirmText'),
      content: this.contentModal,
      confirm: this.translationService.translate('confirmText'),
      cancel: this.translationService.translate('cancelLinkText')
    });
  }

  onSaveConfirmationModalClosed(result: ModalResult): void {
    if (result && result.result) {
      if (this.addLineMode) {
        this.addNewSpecificationLine();
      } else {
        this.saveSpecificationLine();
      }
    }
  }

  private saveSpecificationLine() {
    this.isSaving = true;
    const user = this.store.selectSnapshot(DealerState.currentUser);
    this.assetManagementService.updateSpecificationLine(this.updateSpecificationLine, this.specification.id, user, true).subscribe({
      next: (res) => {
        this.specification = res.data;
        this.specificationLine = this.specification.specificationLines.find((line) => line.id === this.specificationLineId);
        prepareSpecificationLineLabels(this.specification);
        this.initialize();
        this.store.dispatch(new UpdateEditingModeAction(false));
        this.store.dispatch(new UpdateCurrentSpecificationAction(this.specification));
        this.editMode = false;
        this.isSaving = false;
        this.secondRepairLimitAdded = false;
        this.utilityService.showAlert(
          AlertType.success,
          this.translationService.translate('camSaveSuccessText'),
          this.translationService.translate('camSaveSuccessAlertText')
        );
      },

      error: () => {
        this.isSaving = false;
        this.utilityService.showAlert(
          AlertType.critical,
          this.translationService.translate('criticalErrorText'),
          this.translationService.translate('criticalErrorAlertText')
        );
      }
    });
  }

  private addNewSpecificationLine() {
    this.isSaving = true;
    const user = this.store.selectSnapshot(DealerState.currentUser);
    this.assetManagementService.addNewSpecificationLine(this.updateSpecificationLine, this.specification.id, user).subscribe({
      next: (res) => {
        this.specification = res.data;
        this.specificationLine = this.specification.specificationLines.find((line) => line.id === this.specificationLineId);
        prepareSpecificationLineLabels(this.specification);
        this.store.dispatch(new UpdateEditingModeAction(false));
        this.store.dispatch(new UpdateCurrentSpecificationAction(this.specification));
        this.addLineMode = false;
        this.secondRepairLimitAdded = false;
        this.editMode = false;
        this.isSaving = false;
        this.specificationLineForm.markAsUntouched();
        this.initialize();

        this.utilityService.showAlert(
          AlertType.success,
          this.translationService.translate('camAddLineSuccessText'),
          this.translationService.translate('camAddLineSuccessContentText')
        );
      },

      error: () => {
        this.isSaving = false;
        this.utilityService.showAlert(
          AlertType.critical,
          this.translationService.translate('criticalErrorText'),
          this.translationService.translate('criticalErrorAlertText')
        );
      }
    });
  }

  deleteSpecLine() {
    this.showDeleteSpecLineModal();
  }

  showDeleteSpecLineModal(): void {
    this.deleteSpecLineModal.open({
      id: 'deleteSpecLineContent',
      width: 750,
      title: this.translationService.translate('deleteLineText'),
      content: this.deleteSpecLineContent,
      cancel: this.translationService.translate('cancelLinkText'),
      confirm: { text: this.translationService.translate('deleteLineText'), color: 'warn' }
    });
  }

  onDeleteSpecLineModalClosed(result: ModalResult) {
    if (result.result) {
      this.isDeleting = true;
      this.assetManagementService.deleteSpecLine(this.specification.id, this.specificationLine.id, this.username).subscribe({
        next: (res) => {
          this.specification = res.data;
          prepareSpecificationLineLabels(this.specification);
          this.store.dispatch(new UpdateCurrentSpecificationAction(this.specification));
          this.router.navigate(['../', this.specification.specificationLines[0].id], { relativeTo: this.route });
          this.utilityService.showAlert(
            AlertType.success,
            this.translationService.translate('deleteLineText'),
            this.translationService.translate('deleteSpecLineAlertText')
          );
          this.isDeleting = false;
        },

        error: () => {
          this.isDeleting = false;
          this.utilityService.showAlert(
            AlertType.critical,
            this.translationService.translate('criticalErrorText'),
            this.translationService.translate('criticalErrorAlertText')
          );
        }
      });
    }
  }

  addRepairLimit() {
    this.secondRepairLimitAdded = true;
    const newLimit = emptyLimit(2);
    this.specificationLine.limits.push(newLimit);
  }

  onToggleButtonChange(event: MatButtonToggleChange) {
    if (event.value === '10' || event.value === 10) {
      this.specificationLine.type = SpecificationType.REPAIR_RETREAD;
    } else {
      this.specificationLine.type = SpecificationType.REPAIR_ONLY;
    }
    this.specificationLineForm.get('type').patchValue(this.specificationLine.type);
  }

  private initialize() {
    // populating the specification line form group
    this.populateFormGroup();

    // setting the line name and audit dates
    this.lineName = this.specificationLine.label;
    this.isDefault = this.specificationLine.defaultSpec;
    const createdDate = new Date(this.specification.createdDate);
    const modifiedDate = new Date(this.specification.modifiedDate);
    this.createdDate = this.datePipe.transform(createdDate, "yyyy/MM/dd 'at' HH:mm 'EST'", 'EST');
    this.modifiedDate = this.datePipe.transform(modifiedDate, "yyyy/MM/dd 'at' HH:mm 'EST'", 'EST');
  }

  populateFormGroup() {
    this.specificationLineForm.patchValue(this.specificationLine);
    this.specificationLineForm.get('definition').patchValue(this.specificationLine);
    this.specificationLineForm.get('limit0').patchValue(this.specificationLine.repair);
    const limit0 = this.specificationLine.limits[0];
    populateLimitForm(this.specificationLineForm.get('limit0'), limit0);
    if (this.specificationLine.limits.length === 2) {
      const limit1 = this.specificationLine.limits[1];
      populateLimitForm(this.specificationLineForm.get('limit1'), limit1);
    }
    this.specificationLineForm.get('isDefault').patchValue(this.specificationLine.defaultSpec);
  }

  duplicateCheckModal(): void {
    this.duplicateConfirmationModal.open({
      id: 'duplicate-check',
      width: 750,
      title: this.translationService.translate('duplicateCamSpecLineText'),
      content: this.dupContentModal,
      confirm: this.translationService.translate('okText')
    });
  }

  checkIfSpecLineNameIsValid(): void {
    this.specification.specificationLines.forEach((value) => {
      if (
        this.specificationLine !== value &&
        value.label.toLowerCase() === this.specificationLineForm.get('label').value.toString().toLowerCase()
      ) {
        this.specificationLineForm.get('label').markAsTouched();
        this.specificationLineForm.get('label').setErrors({ nameIsInvalid: true });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
